import React, { memo, useEffect, useState } from 'react';
import { Options, Point as HighChartsPoint } from 'highcharts';
import Highcharts from 'highcharts/esm/highcharts.js';
import HighchartsReact from 'highcharts-react-official';
import { CHART_HEIGHT_PIXELS, getChartEvents, getCreditsStyle } from '@components-clean/core/charts/shared/utils';
import Loading from '@components-clean/core/loading/loading';
import { ChartHorizontalLine, LineThresholdSeries, ScatterPlotSeries } from '@interfaces-clean/panels.interface';
import { getThemeColours } from '@utils-clean/highchart-options/highchart-themes';
import { PanelTheme } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import './style.scss';

type ScatterPlotProps = {
    panelData: ScatterPlotSeries[];
    tooltipFormatter: (point: HighChartsPoint) => string;
    horizontalLines?: ChartHorizontalLine[];
    showLegend?: boolean;
    subTitle?: string;
    theme?: PanelTheme;
    title?: string;
    xTitle?: string;
    yTitle?: string;
    legendWidth?: number;
    legendItemWidth?: number;
    thresholdSeries?: LineThresholdSeries[];
    showSubtitle?: boolean;
    isDateRange?: boolean;
    showWaterMark?: boolean;
    chartRef?: React.RefObject<HighchartsReact.RefObject | null>;
    minYOverride?: number;
    maxYOverride?: number;
    plotBands?: Highcharts.XAxisPlotBandsOptions[];
    xAxisFormat?: string;
    scrollLegend?: boolean;
};

type Point = {
    x?: number;
    y?: number;
};

// Highcharts will expect series data in a simple numeric array (1D or 2D) when the data count exceeds
// this threshold. This value is the same as used in legacy for now.
const TURBO_THRESHOLD = 2000;
const THRESHOLD_SERIES_NAME = 'THRESHOLD_SERIES';

const getZIndexForType = (lineType: ScatterPlotSeries['lineType']) => {
    switch (lineType) {
        case 'areasplinerange':
            return 1;
        case 'spline':
            return 2;
        default:
            return 3;
    }
};

const Chart = (props: ScatterPlotProps) => {
    const {
        panelData,
        showLegend,
        subTitle,
        theme = PanelTheme.NONE,
        title,
        xTitle,
        yTitle,
        horizontalLines,
        legendWidth,
        legendItemWidth,
        tooltipFormatter,
        thresholdSeries,
        showSubtitle = true,
        isDateRange,
        showWaterMark = false,
        chartRef,
        minYOverride,
        maxYOverride,
        plotBands,
        xAxisFormat,
        scrollLegend,
    } = props;

    const [options, setOptions] = useState<Options | null>(null);
    const { t } = useTranslation();
    let minY: number | undefined;
    let maxY: number | undefined;

    const processData = (data: Point[] | [number, number, number][]): Point[] | (number | undefined)[][] => {
        if (data.length === 0) return data;
        const isArray = Array.isArray(data[0]);
        const turbo = data.length > TURBO_THRESHOLD;
        if (isArray) {
            return (data as Array<Array<number>>).map((p) => {
                const lowerThresholdY = p[1];
                const upperThresholdY = p[2];
                if (maxY === undefined || (upperThresholdY != null && upperThresholdY > maxY)) maxY = upperThresholdY;
                else if (minY === undefined || (lowerThresholdY != null && lowerThresholdY < minY))
                    minY = lowerThresholdY;
                return p;
            });
        }
        return (data as Array<Point>).map((p) => {
            if (maxY === undefined || (p.y != null && p.y > maxY)) maxY = p.y;
            if (minY === undefined || (p.y != null && p.y < minY)) minY = p.y;
            if (turbo) return [p.x, p.y];
            return p;
        }) as Point[] | (number | undefined)[][];
    };

    const mergeSeriesData = () => {
        const data: Highcharts.SeriesOptionsType[] = panelData.map((f) => ({
            ...f,
            type: f.lineType ?? 'scatter',
            zIndex: getZIndexForType(f.lineType),
            lineColor: f.color,
            marker: {
                symbol: f.marker,
            },
            lineWidth: f.lineWidth,
            fillColor: f.color,
            data: processData(f.data),
            zones: f.zones,
            zoneAxis: f.zoneAxis,
        }));

        if (!!thresholdSeries?.length) {
            // Include the thresholds in the min max Y calculations
            const calculateMaxAndMin = minYOverride === undefined || maxYOverride === undefined;

            data.push(
                ...thresholdSeries.map((ts) => ({
                    ...ts,
                    style: undefined,
                    name: THRESHOLD_SERIES_NAME,
                    zIndex: 1,
                    marker: {
                        symbol: undefined,
                        enabled: false,
                        states: {
                            hover: {
                                enabled: false,
                            },
                        },
                    },
                    dashStyle: ts.style,
                    lineWidth: 1,
                    showInLegend: false,
                    data: calculateMaxAndMin
                        ? processData(ts.data)
                        : ts.data.length < TURBO_THRESHOLD
                          ? ts.data
                          : ts.data.map((p) => {
                                return [p.x, p.y];
                            }),
                })),
            );
        }

        minY = minYOverride === undefined ? minY : minYOverride;
        maxY = maxYOverride === undefined ? maxY : maxYOverride;

        return { data };
    };

    useEffect(() => {
        const { data } = mergeSeriesData();
        setOptions({
            exporting: {
                allowHTML: true,
                chartOptions: {
                    title: { text: title, style: { fontWeight: 'semi-bold' } },
                    subtitle: {
                        text: subTitle,
                        useHTML: true,
                        verticalAlign: 'bottom',
                        align: 'center',
                    },
                    chart: {
                        height: 600,
                    },
                    credits: { enabled: false },
                    legend: {
                        // @ts-ignore - custom prop used to enable scrollbars for legend
                        useScrollbars: false,
                        useHTML: false,
                    },
                },
            },
            chart: {
                zooming: {
                    type: 'xy',
                },
                // potential fix, maybe short-term for chart appearing 'squashed' in fullscreen mode
                height: chartRef?.current?.chart.fullscreen.isOpen ? window.innerHeight : CHART_HEIGHT_PIXELS,
                events: getChartEvents(showWaterMark, showSubtitle, title, subTitle, {
                    printOptions: {
                        chart: {
                            height: 600,
                            width: 800,
                        },
                        legend: {
                            useHTML: false,
                        },
                    },
                    defaultOptions: {
                        chart: {
                            height: CHART_HEIGHT_PIXELS,
                            width: null,
                        },
                        legend: {
                            useHTML: true,
                        },
                    },
                }),
            },
            colors: getThemeColours(theme),
            credits: showWaterMark ? getCreditsStyle(`${t('cm:waterMarkMainPanelText')}`) : { enabled: false },
            subtitle: showSubtitle
                ? {
                      text: subTitle,
                      useHTML: true,
                      verticalAlign: 'bottom',
                      align: 'center',
                  }
                : undefined,
            legend: {
                enabled: showLegend,
                itemWidth: legendItemWidth,
                width: legendWidth,
                // @ts-ignore - custom prop used to enable scrollbars for legend
                useScrollbars: scrollLegend,
                useHTML: scrollLegend,
                ...(scrollLegend
                    ? {
                          itemStyle: {
                              textOverflow: undefined, // when it's HTML the text truncates instead of wrapping
                          },
                      }
                    : {}),
            },
            tooltip: {
                formatter() {
                    const point = this as HighChartsPoint;
                    if (point.series.name === THRESHOLD_SERIES_NAME) return false;

                    return tooltipFormatter(point);
                },
                ...(scrollLegend
                    ? {
                          className: 'custom-tooltip-position',
                          useHTML: true,
                          padding: 1,
                      }
                    : {}),
            },
            plotOptions: {
                series: {
                    cursor: 'pointer',
                    stickyTracking: false,
                    turboThreshold: TURBO_THRESHOLD,
                },
                spline: {
                    marker: { enabled: false },
                },
                areasplinerange: {
                    enableMouseTracking: false,
                    lineWidth: 0,
                    marker: { enabled: false },
                    allowPointSelect: false,
                    fillOpacity: 0.2,
                    opacity: 0.2,
                    showInLegend: false,
                    trackByArea: false,
                    linkedTo: ':previous',
                },
            },
            series: data,
            xAxis: {
                title: { text: xTitle },
                type: isDateRange ? 'datetime' : 'linear',
                crosshair: true,
                plotBands,
                labels: { format: xAxisFormat },
            },
            yAxis: {
                title: { text: yTitle },
                // We need to specify the Y min anx max becayse uit doesn't automatically include the threshold lines
                max: maxY,
                min: minY,
                crosshair: true,
                plotLines:
                    horizontalLines === undefined
                        ? undefined
                        : horizontalLines.map<Highcharts.YAxisPlotLinesOptions>((pl) => ({
                              color: pl.color,
                              dashStyle: pl.style,
                              label: { text: pl.name ?? undefined },
                              value: pl.y,
                              zIndex: 1,
                          })),
            },
        });
    }, [showWaterMark, showSubtitle, subTitle, showLegend, panelData, chartRef?.current?.chart.fullscreen.isOpen]);

    if (options === null) return <Loading />;

    return <HighchartsReact ref={chartRef} highcharts={Highcharts} constructorType="chart" options={options} />;
};
export const ScatterPlot = memo(Chart);
